import { useState, useEffect } from 'react'

const GA_MEASUREMENT_ID = 'G-K3FNL2X6FV'
const CONSENT_KEY = 'offvine_cookie_consent'

type ConsentStatus = 'pending' | 'accepted' | 'declined'

function loadGoogleAnalytics() {
  // Don't load if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
    return
  }

  // Match Google's exact standard implementation
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments) }
  // @ts-expect-error - gtag uses arguments object per Google's spec
  gtag('js', new Date())
  // @ts-expect-error - gtag uses arguments object per Google's spec
  gtag('config', GA_MEASUREMENT_ID)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)
}

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

export default function CookieConsent() {
  const [, setConsentStatus] = useState<ConsentStatus>('pending')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus | null
    if (stored === 'accepted') {
      setConsentStatus('accepted')
      loadGoogleAnalytics()
    } else if (stored === 'declined') {
      setConsentStatus('declined')
    } else {
      setIsVisible(true)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setConsentStatus('accepted')
    setIsVisible(false)
    loadGoogleAnalytics()
  }

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setConsentStatus('declined')
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50">
      <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-center sm:text-left">
          <p>
            We use cookies to analyze site traffic and improve your experience.{' '}
            <button
              onClick={() => {
                const event = new CustomEvent('openPrivacyPolicy')
                window.dispatchEvent(event)
              }}
              className="underline hover:text-gray-300"
            >
              Learn more
            </button>
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm border border-gray-500 rounded hover:bg-gray-800 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm bg-amber-600 rounded hover:bg-amber-700 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}

// Export utility to check consent status
export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem(CONSENT_KEY) === 'accepted'
}

// Export utility to reset consent (for testing or preference updates)
export function resetConsent() {
  localStorage.removeItem(CONSENT_KEY)
  window.location.reload()
}
